import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { Link } from 'react-router-dom';
import AsyncSelect from 'react-select/async';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import { fetchData, postData } from '../../../apiService';
import DeleteIcon from '@mui/icons-material/Delete';
import Select from "react-select";
import CircularProgress from '@mui/material/CircularProgress';
import Modal from '@mui/material/Modal';

const PurchaseOrderCreate = () => {
    const [selectedItems, setSelectedItems] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const { isAuthenticated, companyId, branchId, userId, token, logout, branchstate } = useAuth();
    const [formData, setFormData] = useState({});
    const [errorMessage, setErrorMessage] = useState("");
    const history = useHistory();
    const [supplierOptions, setSupplierOptions] = useState([]);
    const [selectedOption, setSelectedOption] = useState(null);
    const [selectedInvoiceType, setSelectedInvoiceType] = useState(null);
    const [dropdownInvoiceTypeOptions, setDropdownInvoiceTypeOptions] = useState([]);
    const [itemToDeleteIndex, setItemToDeleteIndex] = useState(null);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [itemModalOpen, setItemModalOpen] = useState(false);
    const [pendingItem, setPendingItem] = useState(null);
    const [itemModalQty, setItemModalQty] = useState(1);
    const [itemModalRate, setItemModalRate] = useState(0);
    const [itemsOptions, setItemsOptions] = useState([]);

    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) { logout(); return; }
        fetchInvoicetypeData();
    }, [isAuthenticated, companyId, branchId, userId, token, logout]);

    const fetchInvoicetypeData = () => {
        const today = new Date().toISOString().split('T')[0];
        setFormData(prev => ({ ...prev, BillDate: today, BranchId: branchId, CompanyId: companyId, CreatedBy: userId }));

        fetchData('PurchaseApi', `/InvoiceType/${companyId}/${branchId}`, token)
            .then((response) => {
                if (response?.response_data && Array.isArray(response.response_data))
                    setDropdownInvoiceTypeOptions(response.response_data);
            })
            .catch((error) => console.error('Error fetching invoice type:', error));

        fetchData('PurchaseOrderApi', `/MaxBillNo/${companyId}`, token)
            .then((response) => {
                if (response?.response_data)
                    setFormData(prev => ({ ...prev, BillNumber: response.response_data.BillNumber }));
            })
            .catch((error) => console.error('Error fetching max bill no:', error));
    };

    const fetchOptions = async (inputValue) => {
        if (inputValue.length <= 2) return [];
        try {
            const response = await fetchData('PurchaseApi', `/Party/${companyId}/${inputValue}`, token);
            if (response.responseCode === '1111' && Array.isArray(response.response_data)) {
                setSupplierOptions(response.response_data);
                return response.response_data.map(item => ({ value: item.Id, label: item.Name }));
            }
        } catch (error) { console.error('Error fetching options:', error); }
        return [];
    };

    const handleCustomerSelect = (selected) => {
        setSelectedOption(selected);
        setFormData(prev => ({ ...prev, MasterId: selected ? selected.value : '', CreatedBy: userId }));
        if (!selected) return;
        const supplier = supplierOptions.find(s => s.Id === selected.value);
        const gst = supplier?.GSTNumber ?? '';
        let invoiceTypeId = 2;
        if (gst) invoiceTypeId = gst.substring(0, 2) !== branchstate ? 3 : 1;
        setSelectedInvoiceType({ value: invoiceTypeId, label: invoiceTypeId === 3 ? 'IGST' : invoiceTypeId === 1 ? 'B To B' : 'B To C' });
        setFormData(prev => ({
            ...prev, InvoiceTypeId: invoiceTypeId, GSTNumber: gst,
            CustomerAddress: (supplier?.AddressLine1 ?? '') + ' ' + (supplier?.AddressLine2 ?? ''),
        }));
    };

    const parseAndFix = (val) => parseFloat(val) || 0;

    const calculateItemData = (item) => {
        const rate = parseAndFix(item.Rate);
        const qty = parseAndFix(item.Quantity);
        const amt = (qty * rate).toFixed(2);
        const discAmt = parseAndFix(item.DiscountAmount) > 0
            ? parseAndFix(item.DiscountAmount)
            : (parseAndFix(amt) * parseAndFix(item.DiscountPercentage)) / 100;
        const taxable = (parseAndFix(amt) - discAmt).toFixed(2);
        const cgstRate = parseAndFix(item.GSTRate) / 2;
        const sgstRate = parseAndFix(item.GSTRate) / 2;
        let cgst = 0, sgst = 0, igst = 0;
        if (formData.InvoiceTypeId === 3) {
            igst = ((parseAndFix(taxable) * (cgstRate + sgstRate)) / 100).toFixed(2);
        } else {
            cgst = ((parseAndFix(taxable) * cgstRate) / 100).toFixed(2);
            sgst = ((parseAndFix(taxable) * sgstRate) / 100).toFixed(2);
        }
        const total = parseAndFix(taxable) + parseAndFix(cgst) + parseAndFix(sgst) + parseAndFix(igst);
        return { ...item, Amount: amt, DiscountAmount: discAmt.toFixed(2), TaxableAmount: taxable, CGSTAmount: cgst, SGSTAmount: sgst, IGSTAmount: igst, TotalAmount: total.toFixed(2) };
    };

    const recalculateTotals = (items) => {
        const totals = items.reduce((acc, item) => {
            acc.Quantity += parseAndFix(item.Quantity);
            acc.Amount += parseAndFix(item.Amount);
            acc.DiscountAmount += parseAndFix(item.DiscountAmount);
            acc.TaxableAmount += parseAndFix(item.TaxableAmount);
            acc.CGSTAmount += parseAndFix(item.CGSTAmount);
            acc.SGSTAmount += parseAndFix(item.SGSTAmount);
            acc.IGSTAmount += parseAndFix(item.IGSTAmount);
            acc.TotalAmount += parseAndFix(item.TotalAmount);
            return acc;
        }, { Quantity: 0, Amount: 0, DiscountAmount: 0, TaxableAmount: 0, CGSTAmount: 0, SGSTAmount: 0, IGSTAmount: 0, TotalAmount: 0 });
        const roundOff = Math.round(totals.TotalAmount) - totals.TotalAmount;
        const grandTotal = totals.TotalAmount + roundOff;
        setFormData(prev => ({
            ...prev,
            TotalQuantity: totals.Quantity,
            Amount: totals.Amount.toFixed(2),
            TaxableAmount: totals.TaxableAmount.toFixed(2),
            CGSTAmount: totals.CGSTAmount.toFixed(2),
            SGSTAmount: totals.SGSTAmount.toFixed(2),
            IGSTAmount: totals.IGSTAmount.toFixed(2),
            DiscountAmount: totals.DiscountAmount.toFixed(2),
            RoundOff: roundOff.toFixed(2),
            TotalAmount: grandTotal.toFixed(2),
        }));
    };

    const handleCalculation = (e, index) => {
        const name = e.target.name;
        const value = e.target.value;
        setSelectedItems(prev => {
            const updated = [...prev];
            updated[index] = { ...updated[index], [name]: value };
            const recalculated = updated.map(calculateItemData);
            recalculateTotals(recalculated);
            return recalculated;
        });
    };

    const fetchItemOptions = async (inputValue = '') => {
        if (!inputValue || inputValue.length <= 1) return [];
        try {
            //const response = await fetchData('PurchaseApi', `/Item/${companyId}/${inputValue}`, token);
             const response = await fetchData('SaleApi', `/Item/${companyId}/${companyId}/${inputValue}`, token);
            if (response.responseCode === '1111' && Array.isArray(response.response_data)) {
                setItemsOptions(response.response_data);
                return response.response_data.map(item => ({
                    value: item.Id,
                    label: item.ItemName,
                    PurchaseRate: item.PurchaseRate || 0,
                    GSTRate: (parseFloat(item.CGSTRate || 0) * 2).toFixed(2),
                    HSNCode: item.HSNCode || '',
                    BalanceStock: item.QuantityStock || 0,
                }));
            }
        } catch (error) { console.error('Error fetching items:', error); }
        return [];
    };

    const handleItemSelect = (selected) => {
        if (!selected) return;
        const itemDetail = itemsOptions.find(i => i.Id === selected.value);
        const balanceQuantity = itemDetail?.BalanceStock || 0;
        setPendingItem({ ...selected, BalanceQuantity: balanceQuantity });
        setItemModalQty(1);
        setItemModalRate(selected.PurchaseRate || 0);
        setItemModalOpen(true);
    };

    const handleItemModalConfirm = () => {
        if (!pendingItem) return;
        const newItem = {
            ItemId: pendingItem.value, ItemName: pendingItem.label,
            Quantity: itemModalQty, Rate: itemModalRate,
            GSTRate: pendingItem.GSTRate || 0,
            BalanceQuantity: pendingItem.BalanceQuantity || 0,
            DiscountPercentage: 0, DiscountAmount: 0,
            Amount: 0, TaxableAmount: 0, CGSTAmount: 0, SGSTAmount: 0, IGSTAmount: 0, TotalAmount: 0,
        };
        const calculated = calculateItemData(newItem);
        setSelectedItems(prev => {
            const updated = [...prev, calculated];
            recalculateTotals(updated);
            return updated;
        });
        setItemModalOpen(false);
        setPendingItem(null);
    };

    const openConfirmDialog = (index) => { setItemToDeleteIndex(index); setConfirmDialogOpen(true); };
    const closeConfirmDialog = () => { setConfirmDialogOpen(false); setItemToDeleteIndex(null); };

    const handleDelete = () => {
        if (itemToDeleteIndex !== null) {
            setSelectedItems(prev => {
                const updated = prev.filter((_, i) => i !== itemToDeleteIndex).map(calculateItemData);
                recalculateTotals(updated);
                return updated;
            });
            closeConfirmDialog();
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setIsLoading(true);
        if (!formData.BillNumber) { setErrorMessage('Bill Number is required!'); setIsLoading(false); return; }
        if (!formData.MasterId) { setErrorMessage('Supplier is required!'); setIsLoading(false); return; }
        const payload = {
            PurchaseOrder: { ...formData, CreatedBy: userId, BranchId: branchId, CompanyId: companyId },
            PurchaseOrderDetails: selectedItems
        };
        postData('PurchaseOrderApi', payload, token)
            .then(() => history.push('/PurchaseOrderIndex'))
            .catch((error) => { console.error('Error creating record:', error); setIsLoading(false); });
    };

    const filteredData = selectedItems.filter(item =>
        Object.values(item).some(v => String(v).toLowerCase().includes(searchQuery.toLowerCase()))
    );

    // ── shared styles ──────────────────────────────────────────────────────────
    const inp = { border: "1px solid rgba(0,0,0,0.12)", borderRadius: "7px", fontSize: "12px", height: "28px", width: "100%", padding: "0 8px", outline: "none", backgroundColor: "#fafafa", boxSizing: "border-box" };
    const inpR = { ...inp, textAlign: "right" };
    const card = { backgroundColor: "#ffffff", borderRadius: "10px", boxShadow: "0 2px 10px rgba(0,0,0,0.07)", border: "1px solid rgba(0,0,0,0.06)", marginBottom: "10px", overflow: "hidden" };
    const cardHead = (color = "#2dd4bf") => ({ display: "flex", alignItems: "center", gap: "6px", padding: "6px 12px", borderBottom: "1px solid rgba(0,0,0,0.06)", backgroundColor: `${color}18`, fontSize: "10px", fontWeight: 700, color: "#1a2e2e", textTransform: "uppercase", letterSpacing: "0.5px" });
    const cardBody = { padding: "10px 12px" };
    const lbl = { fontSize: "9.5px", fontWeight: 700, color: "#5a7a7a", textTransform: "uppercase", letterSpacing: "0.5px", display: "block", marginBottom: "3px" };

    return (
        <div style={{ fontFamily: "Segoe UI, sans-serif", fontSize: "12px", width: "100%", padding: "6px 6px 6px", boxSizing: "border-box", backgroundColor: "#f0f4f4" }}>

            {/* ── Page header card ── */}
            <div style={{ ...card, marginBottom: "10px", overflow: "visible" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 12px", flexWrap: "wrap", gap: "8px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                        <span style={{ fontSize: "15px", color: "#2dd4bf" }}>🛒</span>
                        <span style={{ fontWeight: 800, fontSize: "13px", color: "#1a2e2e", fontFamily: "Inter, sans-serif" }}>Purchase Order — Create</span>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <span style={{ fontSize: "11px", fontWeight: 600, color: "#5a7a7a", whiteSpace: "nowrap" }}>
                            Supplier{selectedInvoiceType && <span style={{ color: "#2dd4bf", marginLeft: "6px" }}>({selectedInvoiceType.label})</span>}
                        </span>
                        <AsyncSelect
                            cacheOptions loadOptions={fetchOptions}
                            onChange={handleCustomerSelect} value={selectedOption}
                            placeholder="Search supplier…" isClearable
                            menuPortalTarget={document.body} menuPosition="fixed"
                            styles={{
                                control: (b) => ({ ...b, minHeight: "28px", height: "28px", fontSize: "12px", width: "260px", borderRadius: "7px", borderColor: "rgba(0,0,0,0.12)" }),
                                valueContainer: (b) => ({ ...b, height: "28px", padding: "0 8px" }),
                                indicatorsContainer: (b) => ({ ...b, height: "28px" }),
                                menu: (b) => ({ ...b, fontSize: "12px" }),
                                menuPortal: (b) => ({ ...b, zIndex: 9999 }),
                            }}
                        />
                    </div>
                    <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                        <button onClick={handleSubmit} disabled={isLoading}
                            style={{ height: "28px", padding: "0 16px", borderRadius: "7px", border: "none", backgroundColor: "#2dd4bf", color: "#fff", fontWeight: 700, fontSize: "11px", cursor: "pointer", display: "flex", alignItems: "center", gap: "4px" }}>
                            {isLoading ? <CircularProgress size={14} style={{ color: "#fff" }} /> : "💾"} Save
                        </button>
                        <Link to="/PurchaseOrderIndex" onClick={() => localStorage.setItem("shouldReload", "true")}
                            style={{ height: "28px", padding: "0 14px", borderRadius: "7px", border: "1px solid rgba(0,0,0,0.12)", backgroundColor: "#fff", color: "#5a7a7a", fontWeight: 600, fontSize: "11px", cursor: "pointer", display: "flex", alignItems: "center", textDecoration: "none" }}>
                            ← Back
                        </Link>
                    </div>
                </div>
            </div>

            {errorMessage && (
                <div style={{ marginBottom: "8px", padding: "6px 12px", backgroundColor: "#fef2f2", border: "1px solid #fecaca", borderRadius: "7px", fontSize: "11px", color: "#ef4444" }}>
                    {errorMessage}
                </div>
            )}

            {/* ── Bill details card ── */}
            <div style={card}>
                <div style={cardHead()}>📋 Bill Details</div>
                <div style={{ ...cardBody, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: "10px" }}>
                    <div>
                        <label style={lbl}>Invoice Type</label>
                        <Select options={dropdownInvoiceTypeOptions.map(o => ({ value: o.Id, label: o.Name }))}
                            value={selectedInvoiceType}
                            onChange={(sel) => { setSelectedInvoiceType(sel); setFormData(prev => ({ ...prev, InvoiceTypeId: sel?.value })); }}
                            styles={{
                                control: (b) => ({ ...b, minHeight: "28px", height: "28px", fontSize: "12px", borderRadius: "7px", borderColor: "rgba(0,0,0,0.12)" }),
                                valueContainer: (b) => ({ ...b, height: "28px", padding: "0 8px" }),
                                indicatorsContainer: (b) => ({ ...b, height: "28px" }),
                                menu: (b) => ({ ...b, fontSize: "12px" }),
                            }}
                        />
                    </div>
                    <div><label style={lbl}>Bill Date</label><input type="date" name="BillDate" value={formData.BillDate || ''} onChange={handleChange} style={inp} /></div>
                    <div><label style={lbl}>Bill Number <span style={{ color: "#ef4444" }}>*</span></label><input type="text" name="BillNumber" value={formData.BillNumber || ''} onChange={handleChange} style={inp} placeholder="Bill No" /></div>
                    <div><label style={lbl}>Round Off</label><input type="number" name="RoundOff" value={formData.RoundOff || ''} readOnly style={inpR} /></div>
                    <div><label style={lbl}>Total</label><input type="number" name="TotalAmount" value={formData.TotalAmount || ''} readOnly style={{ ...inpR, fontWeight: 700, color: "#0D47A1" }} /></div>
                </div>
            </div>

            {/* ── Summary bar ── */}
            <div style={card}>
                <div style={{ ...cardBody, display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: "8px" }}>
                    {[["Amount", formData.Amount], ["Discount", formData.DiscountAmount], ["Taxable", formData.TaxableAmount], ["CGST", formData.CGSTAmount], ["SGST", formData.SGSTAmount], ["IGST", formData.IGSTAmount]].map(([label, val]) => (
                        <div key={label} style={{ fontSize: "11px", color: "#5a7a7a" }}>{label}: <strong style={{ color: "#B71C1C" }}>{val || 0}</strong></div>
                    ))}
                </div>
            </div>

            {/* ── Add Item card ── */}
            <div style={card}>
                <div style={cardHead("#6366f1")}>➕ Add Item</div>
                <div style={{ ...cardBody, display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap" }}>
                    <div style={{ flex: "1 1 220px" }}>
                        <AsyncSelect cacheOptions loadOptions={fetchItemOptions} value={null}
                            onChange={handleItemSelect}
                            placeholder="Search item…" isClearable menuPortalTarget={document.body}
                            styles={{
                                control: (b) => ({ ...b, minHeight: "28px", height: "28px", fontSize: "12px", borderRadius: "7px", borderColor: "rgba(0,0,0,0.12)" }),
                                valueContainer: (b) => ({ ...b, height: "28px", padding: "0 8px" }),
                                indicatorsContainer: (b) => ({ ...b, height: "28px" }),
                                menuPortal: (b) => ({ ...b, zIndex: 2000 }),
                                menu: (b) => ({ ...b, fontSize: "12px" }),
                            }}
                        />
                    </div>
                    <input type="text" placeholder="🔍 Filter items…" value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onFocus={() => setIsFocused(true)} onBlur={() => setIsFocused(false)}
                        style={{ height: "28px", border: "1px solid rgba(0,0,0,0.12)", borderRadius: "20px", padding: "0 12px", fontSize: "12px", outline: "none", width: isFocused ? "180px" : "140px", transition: "width 0.3s ease", backgroundColor: "#fafafa" }} />
                </div>
            </div>

            {/* ── Items table card ── */}
            <div style={card}>
                <div style={cardHead("#1F2A38")}>📦 Items ({filteredData.length})</div>
                <div style={{ maxHeight: "320px", overflowY: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px" }}>
                        <thead style={{ backgroundColor: "#1F2A38", color: "#fff", position: "sticky", top: 0 }}>
                            <tr>
                                {["#", "Item", "Qty", "Rate", "Amount", "Disc%", "Disc₹", "Taxable", "GST%", "Bal.Qty", "Total"].map((h, i) => (
                                    <th key={i} style={{ padding: "6px 4px", textAlign: i > 1 ? "right" : "left", fontSize: "10px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.4px", whiteSpace: "nowrap" }}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {filteredData.map((item, index) => (
                                <tr key={index} style={{ backgroundColor: index % 2 === 0 ? "#fff" : "#f8fafc", borderBottom: "1px solid #e5e7eb" }}>
                                    <td style={{ padding: "3px 6px", textAlign: "center", whiteSpace: "nowrap" }}>
                                        {index + 1}
                                        <DeleteIcon style={{ color: "#ef4444", cursor: "pointer", marginLeft: "6px", fontSize: "14px", verticalAlign: "middle" }} onClick={() => openConfirmDialog(index)} />
                                    </td>
                                    <td style={{ padding: "3px 4px", minWidth: "160px", fontWeight: 600 }}>{item.ItemName}</td>
                                    <td style={{ padding: "2px", width: "70px" }}>
                                        <input type="number" value={item.Quantity} name="Quantity" onChange={(e) => handleCalculation(e, index)} style={{ ...inpR, height: "24px", borderRadius: "4px" }} />
                                    </td>
                                    <td style={{ padding: "2px", width: "100px" }}>
                                        <input type="number" value={item.Rate} name="Rate" onChange={(e) => handleCalculation(e, index)} style={{ ...inpR, height: "24px", borderRadius: "4px" }} />
                                    </td>
                                    <td style={{ padding: "2px", width: "100px" }}>
                                        <input value={item.Amount || 0} readOnly style={{ ...inpR, height: "24px", borderRadius: "4px", backgroundColor: "#f8fafa" }} />
                                    </td>
                                    <td style={{ padding: "2px", width: "80px" }}>
                                        <input type="number" value={item.DiscountPercentage || 0} name="DiscountPercentage" onChange={(e) => handleCalculation(e, index)} style={{ ...inpR, height: "24px", borderRadius: "4px" }} />
                                    </td>
                                    <td style={{ padding: "2px", width: "100px" }}>
                                        <input type="number" value={item.DiscountAmount || 0} name="DiscountAmount" onChange={(e) => handleCalculation(e, index)} style={{ ...inpR, height: "24px", borderRadius: "4px" }} />
                                    </td>
                                    <td style={{ padding: "2px", width: "100px" }}>
                                        <input value={item.TaxableAmount || 0} readOnly style={{ ...inpR, height: "24px", borderRadius: "4px", backgroundColor: "#f8fafa" }} />
                                    </td>
                                    <td style={{ padding: "2px", textAlign: "right", width: "60px" }}>
                                        <span style={{ color: "darkmagenta", fontSize: "11px" }}>{item.GSTRate || 0}%</span>
                                    </td>
                                    <td style={{ padding: "2px", textAlign: "right", width: "70px" }}>
                                        <span style={{ color: "#2e7d32", fontWeight: "600", fontSize: "11px" }}>{item.BalanceQuantity || 0}</span>
                                    </td>
                                    <td style={{ padding: "2px", width: "100px" }}>
                                        <input value={item.TotalAmount || 0} readOnly style={{ ...inpR, height: "24px", borderRadius: "4px", backgroundColor: "#f8fafa", fontWeight: 700, color: "#b45309" }} />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* ── Remarks card ── */}
            <div style={card}>
                <div style={cardHead("#f59e0b")}>📝 Remarks</div>
                <div style={cardBody}>
                    <textarea name="Remarks" value={formData.Remarks || ''} onChange={handleChange} rows={2}
                        style={{ border: "1px solid rgba(0,0,0,0.12)", borderRadius: "7px", fontSize: "12px", width: "100%", padding: "6px 8px", resize: "vertical", outline: "none", backgroundColor: "#fafafa", boxSizing: "border-box" }} />
                </div>
            </div>

            {/* ── Delete confirm modal ── */}
            <Modal open={confirmDialogOpen} onClose={closeConfirmDialog} style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                <div style={{ padding: "24px", backgroundColor: "white", borderRadius: "12px", width: "320px", textAlign: "center", boxShadow: "0 8px 32px rgba(0,0,0,0.15)" }}>
                    <div style={{ fontSize: "32px", marginBottom: "8px" }}>🗑️</div>
                    <strong style={{ fontSize: "13px" }}>Delete this item?</strong>
                    <p style={{ fontSize: "12px", color: "#5a7a7a", margin: "8px 0 16px" }}>This row will be removed from the order.</p>
                    <div style={{ display: "flex", gap: "8px", justifyContent: "center" }}>
                        <button onClick={handleDelete} style={{ padding: "6px 20px", borderRadius: "7px", border: "none", backgroundColor: "#ef4444", color: "#fff", fontWeight: 700, fontSize: "12px", cursor: "pointer" }}>Delete</button>
                        <button onClick={closeConfirmDialog} style={{ padding: "6px 20px", borderRadius: "7px", border: "1px solid #e5e7eb", backgroundColor: "#fff", color: "#5a7a7a", fontWeight: 600, fontSize: "12px", cursor: "pointer" }}>Cancel</button>
                    </div>
                </div>
            </Modal>

            {/* ── Item Qty & Rate modal ── */}
            <Modal open={itemModalOpen} onClose={() => setItemModalOpen(false)} style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                <div style={{ padding: "24px", backgroundColor: "white", borderRadius: "12px", width: "340px", boxShadow: "0 8px 32px rgba(0,0,0,0.15)" }}>
                    <strong style={{ fontSize: "13px", display: "block", marginBottom: "12px" }}>Add Item — {pendingItem?.label}</strong>
                    <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                        <div>
                            <label style={lbl}>Quantity</label>
                            <input type="number" value={itemModalQty} onChange={(e) => setItemModalQty(e.target.value)} autoFocus min="0" style={{ ...inpR, height: "32px" }} />
                        </div>
                        <div>
                            <label style={lbl}>Rate</label>
                            <input type="number" value={itemModalRate} onChange={(e) => setItemModalRate(e.target.value)} min="0" style={{ ...inpR, height: "32px" }} />
                        </div>
                        <div style={{ fontSize: "12px", color: "#5a7a7a" }}>
                            GST: <strong style={{ color: "darkmagenta" }}>{pendingItem?.GSTRate || 0}%</strong>
                            &nbsp;&nbsp;Amount: <strong style={{ color: "#1565C0" }}>{(parseFloat(itemModalQty || 0) * parseFloat(itemModalRate || 0)).toFixed(2)}</strong>
                            &nbsp;&nbsp;Stock: <strong style={{ color: "#2e7d32" }}>{pendingItem?.BalanceQuantity || 0}</strong>
                        </div>
                    </div>
                    <div style={{ display: "flex", gap: "8px", justifyContent: "center", marginTop: "16px" }}>
                        <button onClick={handleItemModalConfirm} style={{ padding: "6px 20px", borderRadius: "7px", border: "none", backgroundColor: "#2dd4bf", color: "#fff", fontWeight: 700, fontSize: "12px", cursor: "pointer" }}>Add</button>
                        <button onClick={() => setItemModalOpen(false)} style={{ padding: "6px 20px", borderRadius: "7px", border: "1px solid #e5e7eb", backgroundColor: "#fff", color: "#5a7a7a", fontWeight: 600, fontSize: "12px", cursor: "pointer" }}>Cancel</button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default PurchaseOrderCreate;
